import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo, type ReactNode } from 'react';
import { querySuiteRunStatusSummary } from '@/features/executions/data';
import {
  buildStatusDeltaMap,
  buildStatusMetricCards,
  mapStatusOptionsToMetricOptions,
  resolveMetricTone,
  type MetricCardItem,
} from '@/features/metrics/components/statusMetricCards';
import {
  collapseExecutionStatusCounts,
  type ExecutionStatusCounts,
} from '@/features/shared/utils/projectStatusPresentation';
import { queryKeys } from '@/shared/hooks';
import {
  buildContainsFilter,
  buildDateRangeFilters,
  buildEqFilterValue,
  buildInFilter,
  mergeFilters,
} from '@/ui/composites/grid/gridFilters';
import type { TableStatusOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { SuiteRunStatus, SuiteRunStatusSummaryDto } from '@flowqa/shared';

type DateWindowBounds = {
  startDay: string | null;
  endDay: string | null;
  previousStartDay: string | null;
  previousEndDay: string | null;
};

type UseSuitesPageStatusMetricCardsInput = {
  debouncedPanelSearch: string;
  effectivePanelFilterStatuses: SuiteRunStatus[];
  groupErroredWithFailed: boolean;
  isLatestOnlyMetricMode: boolean;
  panelDateWindow: DateWindowBounds;
  panelFilterSuites: string[];
  panelFilterTags: string[];
  panelStatusOptions: Array<TableStatusOption<SuiteRunStatus>>;
  selectedOrganisationId: string | null | undefined;
  selectedProjectId: string | null;
  showMetricDelta: boolean;
  totalSecondaryValue?: ReactNode;
};

const buildStatusSummaryInput = (filters: GridQueryInput['filters']): GridQueryInput => ({
  page: 1,
  pageSize: 1,
  sorts: [],
  filters,
});

const buildSummaryStatusCounts = (
  summary: SuiteRunStatusSummaryDto | null,
): Partial<ExecutionStatusCounts> => ({
  passed: summary?.passed ?? 0,
  failed: summary?.failed ?? 0,
  errored: summary?.errored ?? 0,
  skipped: summary?.skipped ?? 0,
  untested: summary?.untested ?? 0,
});

export const useSuitesPageStatusMetricCards = ({
  debouncedPanelSearch,
  effectivePanelFilterStatuses,
  groupErroredWithFailed,
  isLatestOnlyMetricMode,
  panelDateWindow,
  panelFilterSuites,
  panelFilterTags,
  panelStatusOptions,
  selectedOrganisationId,
  selectedProjectId,
  showMetricDelta,
  totalSecondaryValue,
}: UseSuitesPageStatusMetricCardsInput): MetricCardItem[] => {
  const panelStatusSummaryFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('suiteName', debouncedPanelSearch),
        buildInFilter('status', [...effectivePanelFilterStatuses]),
        buildInFilter('suiteId', [...panelFilterSuites]),
        buildInFilter('tags', [...panelFilterTags]),
        isLatestOnlyMetricMode
          ? buildEqFilterValue('isLatest', true)
          : buildDateRangeFilters(
              'timestamp',
              panelDateWindow.startDay
                ? dayjs(panelDateWindow.startDay).startOf('day').toISOString()
                : null,
              panelDateWindow.endDay
                ? dayjs(panelDateWindow.endDay).endOf('day').toISOString()
                : null,
            ),
      ),
    [
      debouncedPanelSearch,
      effectivePanelFilterStatuses,
      isLatestOnlyMetricMode,
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelFilterSuites,
      panelFilterTags,
    ],
  );
  const panelStatusSummaryInput = useMemo(
    () => buildStatusSummaryInput(panelStatusSummaryFilters),
    [panelStatusSummaryFilters],
  );
  const panelStatusSummaryFiltersKey = useMemo(
    () => JSON.stringify(panelStatusSummaryInput.filters),
    [panelStatusSummaryInput.filters],
  );
  const panelStatusSummaryQuery = useQuery<SuiteRunStatusSummaryDto>({
    queryKey: queryKeys.suiteRunSummary(
      selectedProjectId,
      selectedOrganisationId ?? null,
      panelStatusSummaryFiltersKey,
    ),
    queryFn: async () =>
      querySuiteRunStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        panelStatusSummaryInput,
      ),
  });
  const previousPanelStatusSummaryFilters = useMemo(
    () =>
      mergeFilters(
        buildContainsFilter('suiteName', debouncedPanelSearch),
        buildInFilter('status', [...effectivePanelFilterStatuses]),
        buildInFilter('suiteId', [...panelFilterSuites]),
        buildInFilter('tags', [...panelFilterTags]),
        buildDateRangeFilters(
          'timestamp',
          panelDateWindow.previousStartDay
            ? dayjs(panelDateWindow.previousStartDay).startOf('day').toISOString()
            : null,
          panelDateWindow.previousEndDay
            ? dayjs(panelDateWindow.previousEndDay).endOf('day').toISOString()
            : null,
        ),
      ),
    [
      debouncedPanelSearch,
      effectivePanelFilterStatuses,
      panelDateWindow.previousEndDay,
      panelDateWindow.previousStartDay,
      panelFilterSuites,
      panelFilterTags,
    ],
  );
  const previousPanelStatusSummaryInput = useMemo(
    () => buildStatusSummaryInput(previousPanelStatusSummaryFilters),
    [previousPanelStatusSummaryFilters],
  );
  const previousPanelStatusSummaryFiltersKey = useMemo(
    () => JSON.stringify(previousPanelStatusSummaryInput.filters),
    [previousPanelStatusSummaryInput.filters],
  );
  const previousPanelStatusSummaryQuery = useQuery<SuiteRunStatusSummaryDto>({
    queryKey: queryKeys.suiteRunSummary(
      selectedProjectId,
      selectedOrganisationId ?? null,
      previousPanelStatusSummaryFiltersKey,
    ),
    enabled: showMetricDelta,
    queryFn: async () =>
      querySuiteRunStatusSummary(
        selectedProjectId,
        selectedOrganisationId ?? undefined,
        previousPanelStatusSummaryInput,
      ),
  });
  const panelStatusSummary = panelStatusSummaryQuery.data ?? null;
  const previousPanelStatusSummary = previousPanelStatusSummaryQuery.data ?? null;
  const panelStatusCounts = useMemo(
    () =>
      collapseExecutionStatusCounts(
        buildSummaryStatusCounts(panelStatusSummary),
        groupErroredWithFailed,
      ),
    [groupErroredWithFailed, panelStatusSummary],
  );
  const previousPanelStatusCounts = useMemo(
    () =>
      collapseExecutionStatusCounts(
        buildSummaryStatusCounts(previousPanelStatusSummary),
        groupErroredWithFailed,
      ),
    [groupErroredWithFailed, previousPanelStatusSummary],
  );

  return useMemo(
    () =>
      buildStatusMetricCards({
        options: mapStatusOptionsToMetricOptions(panelStatusOptions, resolveMetricTone),
        counts: panelStatusCounts,
        deltas: showMetricDelta
          ? buildStatusDeltaMap(panelStatusCounts, previousPanelStatusCounts)
          : undefined,
        total: panelStatusSummary?.total,
        previousCounts: previousPanelStatusCounts,
        previousTotal: showMetricDelta ? previousPanelStatusSummary?.total : undefined,
        loading: panelStatusSummaryQuery.data === undefined && panelStatusSummaryQuery.isFetching,
        showDelta: showMetricDelta,
        totalLabel: 'Suite Runs',
        totalSecondaryValue,
        hideZeroCountStatuses: ['errored', 'untested'],
      }),
    [
      panelStatusCounts,
      panelStatusOptions,
      panelStatusSummary?.total,
      panelStatusSummaryQuery.data,
      panelStatusSummaryQuery.isFetching,
      previousPanelStatusCounts,
      previousPanelStatusSummary?.total,
      showMetricDelta,
      totalSecondaryValue,
    ],
  );
};
